import type { OpeningHourRow } from './foodMasterImages';
import { resolveMasterRecordActive } from './masterRecordStatus';

export const OPENING_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export const OPENING_HOUR_DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export const OPENING_HOUR_DAY_LABELS: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

export type OpeningHourFieldErrors = { openTime?: string; closeTime?: string; day?: string };

export function openingHourDaySortIndex(day: unknown): number {
  const code = String(day ?? '').trim().toLowerCase();
  const idx = OPENING_HOUR_DAY_ORDER.indexOf(code as typeof OPENING_HOUR_DAY_ORDER[number]);
  return idx >= 0 ? idx : 999;
}

export function sortOrderForOpeningHourDay(day: unknown): number {
  const idx = openingHourDaySortIndex(day);
  return idx >= 0 ? idx + 1 : 1;
}

export function sortRowsByOpeningHourDay<T extends { metadata?: Record<string, unknown>; sortOrder?: number }>(
  rows: T[],
): T[] {
  return [...rows].sort((a, b) => {
    const dayDiff = openingHourDaySortIndex(a.metadata?.day) - openingHourDaySortIndex(b.metadata?.day);
    if (dayDiff !== 0) return dayDiff;
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });
}

export function isOpeningHourClosed(metadata?: Record<string, unknown>): boolean {
  return metadata?.isClosed === true || metadata?.isClosed === 'true';
}

/** Strip invalid characters; returns null when input cannot be a time value. */
export function sanitizeOpeningHourTimeInput(raw: string): string | null {
  if (!raw) return '';
  if (!/^[\d:]*$/.test(raw)) return null;
  if (raw.length === 5 && !OPENING_TIME_PATTERN.test(raw)) return null;
  return raw;
}

export function formatOpeningHourDayLabel(day: unknown): string {
  const code = String(day ?? '').trim().toLowerCase();
  if (!code) return '—';
  return OPENING_HOUR_DAY_LABELS[code] || code.toUpperCase();
}

export function formatOpeningHourTimeRange(metadata?: Record<string, unknown>): string {
  if (!metadata) return '—';
  if (metadata.isClosed === true || metadata.isClosed === 'true') {
    return 'Closed all day';
  }
  const open = String(metadata.openTime ?? '').trim();
  const close = String(metadata.closeTime ?? '').trim();
  if (open && close) return `${open} – ${close}`;
  if (open) return `${open} – —`;
  if (close) return `— – ${close}`;
  return '—';
}

export function isOpeningHourRowInvalid(metadata?: Record<string, unknown>): boolean {
  if (!metadata || metadata.isClosed === true || metadata.isClosed === 'true') return false;
  const open = String(metadata.openTime ?? '').trim();
  const close = String(metadata.closeTime ?? '').trim();
  if (!open || !close) return false;
  if (!OPENING_TIME_PATTERN.test(open) || !OPENING_TIME_PATTERN.test(close)) return false;
  return open >= close;
}

export function openingHourDayTaken(
  day: string,
  records: Array<{ _id?: string; metadata?: Record<string, unknown>; isActive?: boolean; description?: string }>,
  editingId?: string,
): boolean {
  const normalized = day.trim().toLowerCase();
  if (!normalized) return false;
  return records.some((row) => {
    if (editingId && row._id === editingId) return false;
    return String(row.metadata?.day ?? '').trim().toLowerCase() === normalized;
  });
}

export function allOpeningHourDaysTaken(
  dayOptions: Array<{ value: string | number }>,
  records: Array<{ _id?: string; metadata?: Record<string, unknown>; isActive?: boolean; description?: string }>,
  editingId?: string,
): boolean {
  if (!dayOptions.length) return false;
  return dayOptions.every((option) => openingHourDayTaken(String(option.value), records, editingId));
}

export function validateDuplicateOpeningHourDay(
  day: string,
  records: Array<{ _id?: string; metadata?: Record<string, unknown>; isActive?: boolean; description?: string }>,
  editingId?: string,
): string | undefined {
  if (!day.trim()) return undefined;
  if (openingHourDayTaken(day, records, editingId)) {
    return `Opening hours for ${formatOpeningHourDayLabel(day)} already exist. Edit the existing record or choose another day.`;
  }
  return undefined;
}

export function validateRestaurantOpeningHours(
  rows: OpeningHourRow[],
): Record<number, OpeningHourFieldErrors> {
  const errors: Record<number, OpeningHourFieldErrors> = {};

  rows.forEach((row, index) => {
    if (row.isClosed) return;

    const open = String(row.openTime || '').trim();
    const close = String(row.closeTime || '').trim();
    const rowErrors: OpeningHourFieldErrors = {};

    if (!open) {
      rowErrors.openTime = 'Opening time is required';
    } else if (!OPENING_TIME_PATTERN.test(open)) {
      rowErrors.openTime = 'Use 24-hour format HH:mm (e.g. 10:00)';
    }

    if (!close) {
      rowErrors.closeTime = 'Closing time is required';
    } else if (!OPENING_TIME_PATTERN.test(close)) {
      rowErrors.closeTime = 'Use 24-hour format HH:mm (e.g. 22:00)';
    }

    if (
      open && close
      && OPENING_TIME_PATTERN.test(open)
      && OPENING_TIME_PATTERN.test(close)
      && open >= close
    ) {
      rowErrors.openTime = 'Must be earlier than closing time';
      rowErrors.closeTime = 'Must be later than opening time (overnight / next-day close is not supported)';
    }

    if (Object.keys(rowErrors).length) {
      errors[index] = rowErrors;
    }
  });

  return errors;
}

export function hasRestaurantOpeningHourErrors(
  errors: Record<number, OpeningHourFieldErrors>,
): boolean {
  return Object.keys(errors).length > 0;
}

export function openingHoursValidationSummary(
  rows: OpeningHourRow[],
  errors: Record<number, OpeningHourFieldErrors>,
): string | undefined {
  if (!hasRestaurantOpeningHourErrors(errors)) return undefined;
  const firstIndex = Number(Object.keys(errors)[0]);
  const day = rows[firstIndex]?.day || 'schedule';
  const msg = errors[firstIndex]?.closeTime || errors[firstIndex]?.openTime;
  return msg ? `${day}: ${msg}` : 'Fix opening hours on the Timing tab';
}

/** Active records grouped by day — surfaces duplicate-day issues on the list screen. */
export function findDuplicateOpeningHourDays(
  rows: Array<{ _id?: string; metadata?: Record<string, unknown>; isActive?: boolean; description?: string }>,
): string[] {
  const seen = new Map<string, number>();
  const duplicates = new Set<string>();
  for (const row of rows) {
    if (!resolveMasterRecordActive(row)) continue;
    const day = String(row.metadata?.day ?? '').trim().toLowerCase();
    if (!day) continue;
    seen.set(day, (seen.get(day) || 0) + 1);
    if ((seen.get(day) || 0) > 1) duplicates.add(day);
  }
  return [...duplicates];
}
